/**
 * CCA other-cloud rows -> an Excel file the user can re-upload under the right
 * cloud. An upload checked against one cloud (say AWS) sends back the rows
 * that belong to another as `Other_cloud_data`; they are kept out of the
 * table and offered here as a download.
 *
 * Same shape as components/eia/otherCloudExport.js (buildOtherCloudSheet /
 * downloadOtherCloudExcel), so the dialog can pick the module by product; only
 * the columns differ. Port of legacy ManagePortfolio/Instances.vue
 * downloadExcel CCA branch (:180-193), with the same library (SheetJS `xlsx`),
 * loaded only when the user clicks.
 *
 * Kept from legacy: one sheet, its CCA columns, their titles and order, the
 * file name `<CLOUD>_Portfolios_<YYYY-MM-DD>.xlsx`. Fixed, as on EIA:
 *  - Quantity / Hours are written as numbers (legacy wrote text, which Excel
 *    flags), blank when missing — never "undefined";
 *  - mixed clouds are named `Other_Clouds`, not after whichever row came first;
 *  - the date is the user's local date, not UTC (legacy could be a day off);
 *  - one width per actual column (legacy set 13 widths for 7 columns).
 */
import { CCA_FIELD } from './ccaFormRules';

/** Header -> how to read it from a server row (legacy :184-192, verbatim titles). */
const COLUMNS = Object.freeze([
  ['UUID', (r) => r?.uuid ?? ''],
  ['Cloud', (r) => r?.cloud_csp ?? ''],
  ['Region', (r) => r?.region ?? ''],
  ['Size', (r) => r?.[CCA_FIELD.INSTANCE_TYPE] ?? ''],
  ['Quantity', (r) => numberOrBlank(r?.[CCA_FIELD.QUANTITY])],
  ['Total number of hours per month', (r) => numberOrBlank(r?.[CCA_FIELD.HOURS])],
  ['Pricing Model', (r) => r?.[CCA_FIELD.PRICING_MODEL] ?? ''],
]);

function numberOrBlank(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? n : '';
}

/** YYYY-MM-DD in the user's own time zone. */
function localDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Everything the file needs, without touching SheetJS.
 *
 * @param {object[]} rows   the server's Other_cloud_data
 * @param {Date} [today]
 * @returns {{ fileName: string, sheetName: string, headers: string[],
 *             data: object[], widths: number[] }}
 */
export function buildOtherCloudSheet(rows, today = new Date()) {
  const clouds = [
    ...new Set(rows.map((r) => String(r?.cloud_csp ?? '').toUpperCase()).filter(Boolean)),
  ];
  const cloud = clouds.length === 1 ? clouds[0] : clouds.length > 1 ? 'Other_Clouds' : 'Cloud';
  const headers = COLUMNS.map(([header]) => header);
  return {
    fileName: `${cloud}_Portfolios_${localDate(today)}.xlsx`,
    // Excel caps sheet names at 31 characters.
    sheetName: cloud.slice(0, 31),
    headers,
    data: rows.map((row) =>
      Object.fromEntries(COLUMNS.map(([header, read]) => [header, read(row)])),
    ),
    // Fit each header, 10–32 characters (the hours title is long).
    widths: headers.map((h) => Math.min(Math.max(h.length + 2, 10), 32)),
  };
}

/** Build the workbook and save it. Throws if the library or the save fails. */
export async function downloadOtherCloudExcel(rows) {
  const XLSX = await import('xlsx');
  const sheet = buildOtherCloudSheet(rows);
  const ws = XLSX.utils.json_to_sheet(sheet.data, { header: sheet.headers });
  ws['!cols'] = sheet.widths.map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
  XLSX.writeFile(wb, sheet.fileName);
}
